import { Link } from "@inertiajs/react";
import { CreditCard, Heart, Check } from "lucide-react";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import StatusBadge from "@/components/shared/StatusBadge";
import ProgressBar from "@/components/shared/ProgressBar";

function PublicDonationPagePreview() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2 mb-1">
          <StatusBadge status="active" />
          <span className="text-xs text-muted-foreground">Spring Read-a-thon 2026 · Room 12</span>
        </div>
        <CardTitle className="text-2xl">Sarah T. is reading for Lincoln Elementary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-primary/10 px-4 py-3 text-center">
            <p className="text-[10px] uppercase tracking-wider text-primary mb-1">Raised</p>
            <p className="font-display text-xl font-semibold text-primary">$75</p>
          </div>
          <div className="rounded-lg bg-accent/10 px-4 py-3 text-center">
            <p className="text-[10px] uppercase tracking-wider text-accent mb-1">Minutes</p>
            <p className="font-display text-xl font-semibold text-accent">135</p>
          </div>
        </div>
        <ProgressBar label="School-wide" detail="$3,210 of $5,000" pct={64} />
        <Button size="lg" className="w-full"><Heart className="h-4 w-4" /> Donate to Sarah</Button>
      </CardContent>
    </Card>
  );
}

function CheckoutPreview() {
  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <div>
          <Label>Pick an amount</Label>
          <div className="grid grid-cols-4 gap-2 mt-2">
            {["$10", "$25", "$50", "$100"].map((a) => (
              <div
                key={a}
                className={`h-10 rounded-md border flex items-center justify-center text-sm font-medium ${a === "$25" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
              >
                {a}
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <Label>Card number</Label>
          <Input placeholder="•••• •••• •••• ••••" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Expiry</Label>
            <Input placeholder="MM/YY" />
          </div>
          <div className="space-y-2">
            <Label>CVC</Label>
            <Input placeholder="•••" />
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="h-3.5 w-3.5 rounded border border-primary bg-primary/20" />
          <span>Add $1.03 to cover the card fee</span>
        </div>
        <Button size="lg" className="w-full">Donate $26.03</Button>
      </CardContent>
    </Card>
  );
}

function ThankYouPreview() {
  return (
    <Card>
      <CardContent className="p-6 text-center space-y-3">
        <div className="mx-auto h-12 w-12 rounded-full bg-success/10 flex items-center justify-center">
          <Check className="h-6 w-6 text-success" strokeWidth={3} />
        </div>
        <p className="font-display text-xl font-semibold text-foreground">Thank you, Karen.</p>
        <p className="text-sm text-muted-foreground">
          Sarah's campaign just got $25.00 closer to the goal.
        </p>
        <Button variant="outline">View receipt</Button>
      </CardContent>
    </Card>
  );
}

export default function MakingADonation() {
  return (
    <>
      <p>
        Someone you know — a niece, a neighbour, a friend's kid — is reading for their school's read-a-thon and shared a link. To donate, click the link, pick an amount, enter your card. No account, no signup, about 30 seconds.
      </p>

      <h2>The donation page</h2>
      <Screenshot caption="What you'll see when you click the link" browser="pagepledge.com/p/dyq8s4-bear-moon-42">
        <div className="max-w-md mx-auto">
          <PublicDonationPagePreview />
        </div>
      </Screenshot>

      <h2>What you'll fill in</h2>
      <p>
        Pick an amount — there are quick buttons for $5, $10, $25, $50 and $100, or choose Other to type your own (minimum $1 CAD). Enter your card details, and you're almost done.
      </p>
      <Screenshot caption="The checkout">
        <div className="max-w-md mx-auto">
          <CheckoutPreview />
        </div>
      </Screenshot>

      <p>
        Before you donate, there are a few optional things you can tick or fill in:
      </p>
      <p>
        <strong>Cover the card fee.</strong> Card payments cost a small fee. Tick this box to cover it so the school gets your whole donation. Most donors do — see <Link href="/help/articles/covering-processing-fees">Covering the processing fee</Link>.
      </p>
      <p>
        <strong>Review any optional extra contribution.</strong> If offered at checkout, its recipient and amount are shown before payment. You can choose whether to add it.
      </p>
      <p>
        <strong>Leave a public message.</strong> "Go Sarah! 📚 — Aunt Karen" is the kind of thing students love to see. Anonymous donors can still leave a message; just your name gets hidden.
      </p>

      <p>
        Hit donate, and we'll charge the card and send the donation straight to the school's account. You'll land on a thank-you page with your receipt.
      </p>
      <Screenshot caption="The thank-you page">
        <div className="max-w-md mx-auto">
          <ThankYouPreview />
        </div>
      </Screenshot>

      <Callout variant="privacy" title="What we store">
        <p>
          Your name, your email (for the receipt), the donation amount, and the last four digits of your card. We <strong>never see</strong> your full card number — it goes from your browser straight to Square.
        </p>
      </Callout>

      <p>
        Within seconds, you'll have a receipt in your inbox. The thank-you page also has a link to your receipt that you can bookmark — see <Link href="/help/articles/donor-receipts-and-refunds">Receipts, refunds, and changing your card</Link>.
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/covering-processing-fees">Covering the processing fee</Link>.</li>
        <li><Link href="/help/articles/anonymous-donations-and-messages">Anonymous donations and messages</Link>.</li>
        <li><Link href="/help/articles/donor-receipts-and-refunds">Receipts and refunds</Link>.</li>
      </ul>
    </>
  );
}
